import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import { FilterBar } from '../../../../components/list';
import { useDebounce } from '../../../../hooks/useDebounce';

const { Search } = Input;

export interface CustomerSegmentFilterValues {
  search?: string;
}

export interface CustomerSegmentFilterBarProps {
  value: CustomerSegmentFilterValues;
  onChange: (value: CustomerSegmentFilterValues) => void;
  onReset: () => void;
}

const CustomerSegmentFilterBar: React.FC<CustomerSegmentFilterBarProps> = ({
  value,
  onChange,
  onReset,
}) => {
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => { valueRef.current = value; }, [value]);
  useLayoutEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const lastCommittedRef = useRef(value.search ?? '');

  const [localSearch, setLocalSearch] = useState(value.search ?? '');
  const debouncedSearch = useDebounce(localSearch, 300);

  // Sync debounced value to parent (passive debounce — fires ~300ms after typing stops)
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  }, [debouncedSearch]);

  // Sync from parent (e.g., external reset)
  useEffect(() => {
    setLocalSearch(value.search ?? '');
  }, [value.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalSearch(val);
    if (val === '') {
      // Clear (X) button — fire immediately
      if (lastCommittedRef.current === '') return;
      lastCommittedRef.current = '';
      onChangeRef.current({ ...valueRef.current, search: undefined });
    }
  };

  const handleSearch = (v: string) => {
    // Enter key or search button — fire immediately (no debounce)
    const trimmed = v.trim();
    setLocalSearch(v);
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  };

  const hasActiveFilters = Boolean(value.search);

  return (
    <FilterBar onReset={hasActiveFilters ? onReset : undefined}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Search</span>
        <Search
          allowClear
          onSearch={handleSearch}
          onChange={handleSearchChange}
          value={localSearch}
          style={{ width: 240 }}
        />
      </div>
    </FilterBar>
  );
};

export default CustomerSegmentFilterBar;
